'use client'
import { type FC } from 'react'
import { Center, Icon, SimpleGrid, Text } from '@chakra-ui/react'
import { MdStorefront } from 'react-icons/md'
import { TbTruckDelivery } from 'react-icons/tb'
import type { PricedShippingOption } from 'api'
import { convertMinorUnitToValue } from 'shared-utils'
import { GrandRadioCard } from 'ui'

interface ShippingMethodSelectorProps {
  options: PricedShippingOption[]
  selectedId?: string
  onChange: (methodId: string) => void
  /** Money formatter from the parent — reused so the currency formatting
   *  matches the rest of the checkout. Takes major units; `option.price` is in
   *  minor units and is converted here. */
  formatCost: (n: number) => string
  /** Rendered instead of a price on a free option. */
  freeLabel: string
}

/**
 * Radio-card list of the delivery options a store has configured under
 * `shippingProviderConfig.defaultOptions.methods`.
 *
 * Used only when the store configures its own options — the implicit
 * delivery/pickup pair keeps rendering through `DeliveryMethodSelector`, whose
 * copy brands already override.
 */
export const ShippingMethodSelector: FC<ShippingMethodSelectorProps> = ({
  options,
  selectedId,
  onChange,
  formatCost,
  freeLabel
}) => {
  if (!options.length) {
    return null
  }

  return (
    <GrandRadioCard.Root
      onChange={(next) => onChange(next as string)}
      size="md"
      value={selectedId}
      variant="outline"
    >
      <SimpleGrid columns={{ base: 1, md: Math.min(options.length, 2) }} gap={4} w="100%">
        {options.map((option) => (
          <GrandRadioCard.Item
            alignItems="center"
            display="flex"
            justifyContent="center"
            key={option.id}
            textAlign="center"
            value={option.id}
          >
            <Center flexDirection="column" gap={3} h="100%">
              <Icon
                as={option.kind === 'PICKUP' ? MdStorefront : TbTruckDelivery}
                boxSize={6}
                color="blue.500"
              />
              <GrandRadioCard.ItemContent align="center">
                <GrandRadioCard.ItemText textAlign="center">{option.label}</GrandRadioCard.ItemText>
                {option.description ? (
                  <GrandRadioCard.ItemDescription textAlign="center">
                    {option.description}
                  </GrandRadioCard.ItemDescription>
                ) : null}
                <Text fontSize="sm" fontWeight="semibold" mt={1} textAlign="center">
                  {option.isFree ? freeLabel : formatCost(convertMinorUnitToValue(option.price))}
                </Text>
              </GrandRadioCard.ItemContent>
            </Center>
          </GrandRadioCard.Item>
        ))}
      </SimpleGrid>
    </GrandRadioCard.Root>
  )
}
